import { DatePipe, NgClass } from '@angular/common';
import { Component, effect, input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { strToColor } from '@reflact/tsutil';
import { HistoryItemResponse } from '@shared/index';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { SupervisorService } from '../supervisor.service';
import { SupervisorThreadBroadcastService } from './supervisor-thread-broadcast.service';

@Component({
  selector: 'app-supervisor-threads',
  imports: [NgClass, DatePipe, FormsModule, TableModule, CheckboxModule, ButtonModule, TagModule, TooltipModule],
  templateUrl: './supervisor-threads.component.html',
  styleUrl: './supervisor-threads.component.scss'
})
export class SupervisorThreadsComponent {
  // kept for future lazy-loading compatibility
  public readonly viewPort = input<{ height: number; width: number } | null>(null);
  public readonly filterOnlineOnly = input<boolean>(false);
  public readonly filterWithNotifications = input<boolean>(false);
  public readonly filterMinMessages = input<number>(0);

  constructor(
    public supervisorService: SupervisorService,
    public threadBroadcastService: SupervisorThreadBroadcastService,
    private router: Router,
    private rss: RouteShareService
  ) {
    // Reload from page 0 whenever the min-messages filter changes
    effect(() => {
      const minMsg = this.filterMinMessages();
      void this.supervisorService.loadThreadsPage(this.rss.botConfig._id, 0, 20, minMsg);
    });
  }

  public async onLazyLoad(event: TableLazyLoadEvent): Promise<void> {
    await this.supervisorService.loadThreadsPage(
      this.rss.botConfig._id,
      event.first ?? 0,
      event.rows ?? 20,
      this.filterMinMessages()
    );
  }

  public get displayedThreads(): HistoryItemResponse[] {
    let threads = this.supervisorService.filteredThreads;
    if (this.filterOnlineOnly()) {
      threads = threads.filter(t => this.supervisorService.isThreadActive(t.threadId));
    }
    if (this.filterWithNotifications()) {
      threads = threads.filter(t => this.supervisorService.notificationCountAll(t.threadId) > 0);
    }
    const minMsg = this.filterMinMessages();
    if (minMsg > 0) {
      // server already filtered; this guards against stale cached counts from previous pages
      threads = threads.filter(t => this.supervisorService.getMessageCountPerThread(t.threadId) >= minMsg);
    }
    return threads;
  }

  public getThreadColor(userId: string): string {
    return strToColor(userId.substring(6));
  }

  public isThreadSelected(thread: HistoryItemResponse): boolean {
    return !!(this.supervisorService.selectedThread?.[0] &&
      this.supervisorService.isHistoryItemPartOfTheSameThread(thread, this.supervisorService.selectedThread[0]));
  }

  protected threadOpen(threadId: string): boolean {
    return this.supervisorService.isThreadActive(threadId);
  }

  public selectThreadForBroadcast(threadId: string): void {
    this.threadBroadcastService.toggleBroadcastForThread(threadId);
    this.supervisorService.selectedThread = undefined;
    void this.router.navigate(['/bot-admin', this.rss.botConfig._id, 'supervisor']);
  }

  protected selectThread(botId: string, threadId: string): void {
    if (this.threadBroadcastService.selectedThreadsSet.size > 0) {
      void this.router.navigate(['/bot-admin', this.rss.botConfig._id, 'supervisor']);
      this.threadBroadcastService.toggleBroadcastForThread(threadId);
      return;
    }
    void this.router.navigate(['/bot-admin', botId, 'supervisor', threadId]);
  }

  public selectAll(): void {
    this.threadBroadcastService.toggleAllThreadsForBroadcast();
    this.supervisorService.selectedThread = undefined;
    void this.router.navigate(['/bot-admin', this.rss.botConfig._id, 'supervisor']);
  }
}
